import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { ParamsGetAccount } from '@vindr/services/apis/account.api';
import { DEBOUNCE_WAIT } from '@vindr/services/constants/common.constant';
import { USER_STATUS } from '@vindr/services/constants/filter.constant';
import { filterValidValue, uniquePushArray } from '@vindr/utils/helpers/common.helper';
import useRoleMemo from '@vindr/utils/hooks/useRoleMemo';
import { DefaultOptionType } from 'antd/es/select';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormItem from '../FormItem';
import { FormSelectProps } from '../templates/FormSelect';

interface Technician<T> extends FormSelectProps<T> {
  institutions?: ParamsGetAccount['institutions'];
  selected: DefaultOptionType;
}

const Technician = <T,>({
  name = 'technician_id',
  label = 'Technician',
  placeholder = 'Select technician',
  rules,
  required = true,
  onChange,
  disabled,
  multiple,
  institutions,
  selected,
  ...props
}: Technician<T>) => {
  const { t } = useTranslation('Vindoc');
  const [searchValue, setSearchValue] = useState();

  const { data, isInitialLoading } = useGetAccounts({
    offset: 0,
    limit: 100,
    statuses: [USER_STATUS.ACTIVE],
    institutions,
    ...filterValidValue<ParamsGetAccount>(
      { fullname: searchValue },
      { invalidList: [null, undefined, ''] }
    ),
  });

  const accounts = useMemo(() => {
    return data?.data;
  }, [data?.data]);

  const rulesMemo = useRoleMemo({ required, rules, disabled });

  const debouncedSearch = useCallback(
    debounce((searchText) => {
      setSearchValue(searchText);
    }, DEBOUNCE_WAIT),
    []
  );

  const handleSearch = useCallback(
    (searchText) => {
      debouncedSearch(searchText);
    },
    [debouncedSearch]
  );

  return (
    <FormItem.Select
      showSearch
      className="w-full"
      disabled={disabled}
      dropdownMatchSelectWidth={false}
      label={label}
      listHeight={200} //? Fix when search, accounts long make dropdown position on top
      loading={isInitialLoading}
      name={name}
      placeholder={t(placeholder)}
      rules={rulesMemo}
      size="small"
      suffixIcon={<CaretDownOutlined />}
      wrapperClassName="w-full"
      options={uniquePushArray<DefaultOptionType>(
        accounts?.map((user) => ({ value: user?.id, label: generateUserLabel(user) })),
        selected
      )}
      onSearch={handleSearch}
      {...props}
    />
  );
};

export default Technician;

export const generateUserLabel = (
  user: { fullname: string; username?: string } | { fullname?: string; username: string }
) => {
  const fullName = user?.fullname;
  const userName = user?.username;

  if (!fullName && userName) return userName;
  if (fullName && !userName) return fullName;
  return `${user?.fullname} (${user?.username})`;
};
